import { definePreset } from '@primeuix/themes';
import Material from '@primeuix/themes/material';
import { BD_MESSAGE_TOKENS, BD_SEMANTIC_TOKENS } from './bd-colors';
import { TABLE_TOOLBAR_HEADER } from './table-toolbar';

export const PrimeNgAdminTheme = definePreset(Material, {
  semantic: {
    bd: BD_SEMANTIC_TOKENS,
    primary: {
      50: '{orange.50}',
      100: '{orange.100}',
      200: '{orange.200}',
      300: '{orange.300}',
      400: '{orange.400}',
      500: '{orange.500}',
      600: '{orange.600}',
      700: '{orange.700}',
      800: '{orange.800}',
      900: '{orange.900}',
      950: '{orange.950}',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{gray.50}',
          100: '{gray.100}',
          200: '{gray.200}',
          300: '{gray.300}',
          400: '{gray.400}',
          500: '{gray.500}',
          600: '{gray.600}',
          700: '{gray.700}',
          800: '{gray.800}',
          900: '{gray.900}',
          950: '{gray.950}',
        },
      },
    },
  },
  components: {
    message: BD_MESSAGE_TOKENS,
    dialog: {
      css: ({ dt }) => `

        .p-dialog-content:has(.sticky-footer) {
          margin-bottom: 80px;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .p-dialog-content .sticky-footer {
          margin-top: auto;                               /* pushes to bottom when content is short */
          position: fixed;                                /* stays visible when content is long */
          left: 0;
          bottom: 0;                                      /* push the div down to hide the scrolling content */
          flex-shrink: 0;                                 /* prevents footer from shrinking */
          background: ${dt('dialog-background')};         /* covers scrolling content behind it */
          padding: var(--p-overlay-modal-padding);
        }

        .p-dialog-content .has-overflow-shadow {
          box-shadow: 0 -6px 6px -6px rgba(0, 0, 0, 0.3);
        }
      `,
    },
    button: {
      extend: {
        appShell: {
          background: 'var(--app-shell-color)',
          hoverBackground:
            'color-mix(in srgb, var(--app-shell-color), black 15%)',
          activeBackground:
            'color-mix(in srgb, var(--app-shell-color), black 25%)',
          borderColor: 'var(--app-shell-color)',
          hoverBorderColor:
            'color-mix(in srgb, var(--app-shell-color), black 15%)',
          activeBorderColor:
            'color-mix(in srgb, var(--app-shell-color), black 25%)',
          color: 'var(--app-shell-contrast-color)',
          hoverColor: 'var(--app-shell-contrast-color)',
          activeColor: 'var(--app-shell-contrast-color)',
        },
      },
      css: ({ dt }) => `
        .p-dialog-maximize-button {
          background: transparent;
          border-color: transparent;
          color: ${dt('button-text-secondary-color')};
        }

        .p-button-appShell {
          background: ${dt('button.app.shell.background')};
          border-color: ${dt('button.app.shell.border.color')};
          color: ${dt('button.app.shell.color')};
        }
        .p-button-appShell:not(:disabled):hover {
          background: ${dt('button.app.shell.hover.background')};
          border-color: ${dt('button.app.shell.hover.border.color')};
          color: ${dt('button.app.shell.hover.color')};
        }
        .p-button-appShell:not(:disabled):active {
          background: ${dt('button.app.shell.active.background')};
          border-color: ${dt('button.app.shell.active.border.color')};
          color: ${dt('button.app.shell.active.color')};
        }
        .p-button-appShell:focus-visible {
          background: ${dt('button.app.shell.active.background')};
          border-color: ${dt('button.app.shell.active.border.color')};
        }
      `,
    },
    panel: {
      css: () => `
        .p-panel {
          border-color: transparent;
        }

        .p-panel-collapsed .p-panel-content-container {
          height: 0px;
        }
      `,
    },
    toolbar: {
      css: () => `
        .p-toolbar {
          background: var(--p-surface-0);
        }

        .p-toolbar h1 {
          font-weight: 600;
          font-size: var(--text-xl); /* 1.25rem (20px) */
          line-height: var(--text-xl--line-height); /* calc(1.75 / 1.25) */
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      `,
    },
    menubar: {
      css: () => `
        .p-menubar {
          border-color: #fff;
          border-radius: 0;
        }
      `,
    },
    panelmenu: {
      panel: {
        first: {
          topBorderRadius: '0',
        },
        last: {
          bottomBorderRadius: '0',
        },
      },
      css: () => `
        .p-panelmenu-panel:has(.p-panelmenu-header-active) {
          margin: 0;
        }
      `,
    },
    datatable: {
      header: TABLE_TOOLBAR_HEADER,
      css: () => `
        .p-datatable .p-datatable-tbody > tr {
          transition: background-color 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        .p-datatable .p-datatable-tbody > tr:hover,
        .p-datatable.p-datatable-striped .p-datatable-tbody > tr:hover {
          background: var(--p-primary-50);
        }
        @media (prefers-reduced-motion: reduce) {
          .p-datatable .p-datatable-tbody > tr { transition: none; }
        }
      `,
    },
    treetable: {
      header: TABLE_TOOLBAR_HEADER,
      css: () => `
        .p-treetable .p-treetable-tbody > tr {
          transition: background-color 250ms cubic-bezier(0, 0, 0.2, 1);
        }
        .p-treetable .p-treetable-tbody > tr:hover {
          background: var(--p-primary-50);
        }
        @media (prefers-reduced-motion: reduce) {
          .p-treetable .p-treetable-tbody > tr { transition: none; }
        }
      `,
    },
  },
  css: () => `
    .p-select-overlay,
    .p-multiselect-overlay {
      width: 0;
    }

    li.p-select-option > span {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  `,
});
